/**
 * The attacks & mugs report (website roadmap S30 — torn.report's Attacks pages) from the player's OWN fights and log:
 * daily attacks made and taken, results, respect, mugs (money and against whom) and a row per opponent. Pure and
 * browser-safe; tested in packages/shared/test/attacks-report.test.ts. The page is apps/web/src/app/pages/attacks.ts.
 *
 * Mug money comes only from Torn's own log lines ("Attack mug", "Attack mug receive"); Torn's fight list says who was
 * mugged but not how much. A mug the log doesn't give an amount for stays unknown (null) — never guessed. Torn doesn't
 * document its log lines' `data`, so lines are read leniently (several names for the same thing).
 */
import { inRange, type DateRange } from './log-kit';
import type { LogLike } from './crimes-report';

/** A fight as the website reads it (v1 `attacks`; apps/web read.ts `Attack` fits). */
export interface FightLike {
  id: string;
  /** ms */
  started: number;
  ended?: number | null;
  attacker: { id: number | null; name: string; faction?: string };
  defender: { id: number | null; name: string; faction?: string };
  result: string;
  /** Respect you gained (0 for fights on you). */
  respect: number;
  /** Did you attack (true) or were you attacked? */
  outgoing: boolean;
  /** Did you come out on top (won your attack / held off theirs)? */
  won: boolean;
  fairFight?: number | null;
  chain?: number;
}

export type MugDir = 'out' | 'in';

/** A mug line of your log: you mugged someone ('out') or someone mugged you ('in'). */
export interface MugLine {
  id: string;
  at: number;
  dir: MugDir;
  /** Money, as the line gives it (null: the line doesn't say). */
  amount: number | null;
  /** The other player's id, as the line gives it. */
  opponent: number | null;
  title: string;
}

const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v.trim())) return Number(v);
  return null;
};

function firstNum(d: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const k of keys) {
    const v = num(d[k]);
    if (v !== null) return v;
  }
  return null;
}

/** A player id from a line's data: a number, a numeric string, or an object with an id. */
function firstId(d: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const k of keys) {
    const raw = d[k];
    const v = raw && typeof raw === 'object' ? num((raw as Record<string, unknown>).id ?? (raw as Record<string, unknown>).user_id) : num(raw);
    if (v !== null && Number.isInteger(v) && v > 0) return v;
  }
  return null;
}

const MONEY_KEYS = ['money_mugged', 'mugged', 'money', 'money_gained', 'money_lost', 'money_stolen', 'amount', 'cash'] as const;
const DEFENDER_KEYS = ['defender', 'defender_id', 'target', 'target_id', 'victim', 'victim_id'] as const;
const ATTACKER_KEYS = ['attacker', 'attacker_id', 'mugger', 'mugger_id'] as const;

/** A log line about a mug (yours or on you), else null. Trick or Treat and anything that isn't a mug are left out. */
export function readMugLine(l: LogLike): MugLine | null {
  const t = (l.title ?? '').toLowerCase();
  if (!/\bmug(ged|ging)?\b/.test(t) || /trick or treat/.test(t)) return null;
  const dir: MugDir = /receive|\bby\b|against you|defend/.test(t) ? 'in' : 'out';
  const d = l.data ?? {};
  const amount = firstNum(d, MONEY_KEYS);
  const opponent = firstId(d, dir === 'out' ? DEFENDER_KEYS : ATTACKER_KEYS) ?? firstId(d, ['user', 'user_id', 'player', 'player_id']);
  return { id: l.id, at: l.at, dir, amount: amount === null ? null : Math.abs(amount), opponent, title: l.title };
}

/** A mug, from your fights, your log or both: who, when, and the money (null when the log doesn't say). */
export interface Mug {
  at: number;
  dir: MugDir;
  opponent: { id: number | null; name: string; faction: string };
  amount: number | null;
  /** The fight (Torn's attack code) and the log line it came from, where known. */
  fightId: string | null;
  logId: string | null;
}

const isMugged = (f: FightLike) => /^mugged$/i.test(f.result.trim());
const fightAt = (f: FightLike) => f.ended ?? f.started;
const other = (f: FightLike) => (f.outgoing ? f.defender : f.attacker);

/**
 * Every mug: the fights Torn calls "Mugged", each joined with its log line (same direction, the same opponent where the
 * line names one, the nearest in time within `windowMs` — Torn writes the line as the fight ends), plus log lines with no
 * fight in the list (older than the fights known). Newest first.
 */
export function joinMugs(fights: readonly FightLike[], lines: readonly MugLine[], windowMs = 5 * 60_000): Mug[] {
  const names = new Map<number, { name: string; faction: string }>();
  for (const f of fights) for (const p of [f.attacker, f.defender]) if (p.id && p.name && !names.has(p.id)) names.set(p.id, { name: p.name, faction: p.faction ?? '' });
  const mugFights = fights.filter(isMugged);
  const used = new Set<string>();
  const byFight = new Map<string, MugLine>();
  const loose: MugLine[] = [];
  for (const l of [...lines].sort((a, b) => b.at - a.at)) {
    const fits = mugFights.filter((f) => !used.has(f.id) && (f.outgoing ? 'out' : 'in') === l.dir && Math.abs(fightAt(f) - l.at) <= windowMs && (l.opponent === null || other(f).id === l.opponent));
    const pick = fits.sort((a, b) => Math.abs(fightAt(a) - l.at) - Math.abs(fightAt(b) - l.at))[0];
    if (pick) {
      used.add(pick.id);
      byFight.set(pick.id, l);
    } else loose.push(l);
  }
  const out: Mug[] = mugFights.map((f) => {
    const l = byFight.get(f.id);
    const o = other(f);
    return { at: fightAt(f), dir: f.outgoing ? 'out' : 'in', opponent: { id: o.id, name: o.name, faction: o.faction ?? '' }, amount: l?.amount ?? null, fightId: f.id, logId: l?.id ?? null };
  });
  for (const l of loose) {
    const n = l.opponent ? names.get(l.opponent) : undefined;
    out.push({ at: l.at, dir: l.dir, opponent: { id: l.opponent, name: n?.name ?? '', faction: n?.faction ?? '' }, amount: l.amount, fightId: null, logId: l.id });
  }
  return out.sort((a, b) => b.at - a.at);
}

// ---------------------------------------------------------------- sums

export interface MugSum {
  count: number;
  /** Money of the mugs whose amount is known. */
  money: number;
  /** Mugs whose amount isn't known (no log line, or the line doesn't say). */
  unknown: number;
  largest: number | null;
}

export function mugSum(mugs: readonly Mug[], dir: MugDir): MugSum {
  const mine = mugs.filter((m) => m.dir === dir);
  const known = mine.map((m) => m.amount).filter((x): x is number => x !== null);
  return { count: mine.length, money: known.reduce((a, b) => a + b, 0), unknown: mine.length - known.length, largest: known.length ? Math.max(...known) : null };
}

export interface AttackTotals {
  made: number;
  won: number;
  taken: number;
  held: number;
  respect: number;
  /** Respect per attack that earned any. */
  respectPerHit: number | null;
  bestRespect: number | null;
  fairFight: number | null;
  mugged: MugSum;
  muggedYou: MugSum;
}

export function attackTotals(fights: readonly FightLike[], mugs: readonly Mug[]): AttackTotals {
  const out = fights.filter((f) => f.outgoing);
  const inc = fights.filter((f) => !f.outgoing);
  const respect = out.reduce((s, f) => s + (f.respect || 0), 0);
  const earned = out.filter((f) => f.respect > 0);
  const ffs = out.map((f) => f.fairFight).filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
  return {
    made: out.length,
    won: out.filter((f) => f.won).length,
    taken: inc.length,
    held: inc.filter((f) => f.won).length,
    respect,
    respectPerHit: earned.length ? respect / earned.length : null,
    bestRespect: earned.length ? Math.max(...earned.map((f) => f.respect)) : null,
    fairFight: ffs.length ? ffs.reduce((a, b) => a + b, 0) / ffs.length : null,
    mugged: mugSum(mugs, 'out'),
    muggedYou: mugSum(mugs, 'in'),
  };
}

/** How fights ended, by Torn's own result: how many of yours, how many on you. Most first. */
export function resultRows(fights: readonly FightLike[]): Array<{ result: string; out: number; in: number }> {
  const m = new Map<string, { result: string; out: number; in: number }>();
  for (const f of fights) {
    const key = (f.result || 'Unknown').trim();
    const r = m.get(key.toLowerCase()) ?? { result: key, out: 0, in: 0 };
    if (f.outgoing) r.out++;
    else r.in++;
    m.set(key.toLowerCase(), r);
  }
  return [...m.values()].sort((a, b) => b.out + b.in - (a.out + a.in) || a.result.localeCompare(b.result));
}

/** One opponent: your attacks on them and theirs on you, respect, and the money either way. */
export interface OpponentRow {
  id: number;
  name: string;
  faction: string;
  hits: number;
  won: number;
  respect: number;
  /** Their attacks on you, and how many you held off. */
  theirs: number;
  held: number;
  /** Money you took from them / they took from you (known amounts), and how many mugs each way. */
  mugged: number;
  mugs: number;
  lost: number;
  muggedYou: number;
  /** Mugs either way whose money the log doesn't give. */
  mugsUnknown: number;
  muggedYouUnknown: number;
  last: number;
}

/** A row per opponent (stealthed attackers, with no id, are left out), most fights first. */
export function byOpponent(fights: readonly FightLike[], mugs: readonly Mug[]): OpponentRow[] {
  const m = new Map<number, OpponentRow>();
  const row = (p: { id: number | null; name: string; faction?: string }, at: number): OpponentRow | null => {
    if (!p.id) return null;
    let r = m.get(p.id);
    if (!r) m.set(p.id, (r = { id: p.id, name: p.name, faction: p.faction ?? '', hits: 0, won: 0, respect: 0, theirs: 0, held: 0, mugged: 0, mugs: 0, lost: 0, muggedYou: 0, mugsUnknown: 0, muggedYouUnknown: 0, last: 0 }));
    if (!r.name && p.name) r.name = p.name;
    if (!r.faction && p.faction) r.faction = p.faction;
    r.last = Math.max(r.last, at);
    return r;
  };
  for (const f of fights) {
    const r = row(other(f), f.started);
    if (!r) continue;
    if (f.outgoing) {
      r.hits++;
      if (f.won) r.won++;
      r.respect += f.respect || 0;
    } else {
      r.theirs++;
      if (f.won) r.held++;
    }
  }
  // Mugs with no fight in the list (log lines older than the fights known) still count for their opponent.
  for (const g of mugs) {
    const r = row(g.opponent, g.at);
    if (!r) continue;
    if (g.dir === 'out') {
      r.mugs++;
      r.mugged += g.amount ?? 0;
      if (g.amount === null) r.mugsUnknown++;
    } else {
      r.muggedYou++;
      r.lost += g.amount ?? 0;
      if (g.amount === null) r.muggedYouUnknown++;
    }
  }
  return [...m.values()].sort((a, b) => b.hits + b.theirs - (a.hits + a.theirs) || b.last - a.last);
}

/** Fights (or mugs) inside a range. */
export const fightsIn = <T extends FightLike>(fights: readonly T[], r: DateRange): T[] => fights.filter((f) => inRange(f.started, r));
export const mugsIn = (mugs: readonly Mug[], r: DateRange): Mug[] => mugs.filter((m) => inRange(m.at, r));

// ---------------------------------------------------------------- CSV

const iso = (ms: number) => new Date(ms).toISOString().replace('T', ' ').slice(0, 19);

/** The fights as CSV rows (with the money of each mug where the log says it). */
export function fightsCsvRows(fights: readonly FightLike[], mugs: readonly Mug[]): { header: string[]; rows: Array<Array<string | number | null>> } {
  const money = new Map(mugs.filter((m) => m.fightId).map((m) => [m.fightId!, m.amount]));
  return {
    header: ['time_utc', 'direction', 'attacker_id', 'attacker', 'defender_id', 'defender', 'result', 'respect', 'fair_fight', 'chain', 'money_mugged', 'attack_code'],
    rows: fights.map((f) => [iso(f.started), f.outgoing ? 'made' : 'on you', f.attacker.id, f.attacker.name, f.defender.id, f.defender.name, f.result, f.respect || 0, f.fairFight ?? null, f.chain ?? null, money.get(f.id) ?? null, f.id]),
  };
}

export function mugsCsvRows(mugs: readonly Mug[]): { header: string[]; rows: Array<Array<string | number | null>> } {
  return {
    header: ['time_utc', 'direction', 'player_id', 'player', 'money', 'attack_code', 'log_id'],
    rows: mugs.map((m) => [iso(m.at), m.dir === 'out' ? 'you mugged' : 'mugged you', m.opponent.id, m.opponent.name, m.amount, m.fightId, m.logId]),
  };
}

// ---------------------------------------------------------------- all time (Torn's personal stats)

/** Torn's own all-time attack counters (v1 personal stats) the report shows, in order. */
export const ATTACK_ALL_TIME: ReadonlyArray<readonly [string, string, 'n' | '$']> = [
  ['attackswon', 'Attacks won', 'n'],
  ['attackslost', 'Attacks lost', 'n'],
  ['attacksdraw', 'Attacks drawn', 'n'],
  ['attacksassisted', 'Assists', 'n'],
  ['defendswon', 'Defends won', 'n'],
  ['defendslost', 'Defends lost', 'n'],
  ['moneymugged', 'Money mugged', '$'],
  ['largestmug', 'Largest mug', '$'],
  ['respectforfaction', 'Respect for your faction', 'n'],
  ['bestkillstreak', 'Best kill streak', 'n'],
];
